import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useNavigationType} from 'react-router-dom';
import {PerfectStoreSetUp} from '@/containers';
import PerfectStoreWizardLayout from '@/containers/PerfectStore/PerfectStoreWizardLayout';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  perfectStoreInitialFormData,
  resetPerfectStore,
  setPerfectStoreFormData
} from '@/store/slices/perfectStoreSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {ROUTES} from '@/utils/constants/routesConstants';
import {usePerfectStore} from '@/hooks/usePerfectStore';
import {getModal} from '@/utils/CommonModal';
import {
  getPerfectStoreCancelModal,
  isPerfectStoreSetupInvalid
} from '@/pages/PerfectStoreUtils';
import {getPerfectStoreWizardReviewRoute} from '@/helpers/perfectStoreUtils';
import {
  CREATE_PERFECT_STORE_TITLE,
  isStoreExecutionPepIrisTarget
} from '@/utils/constants/perfectStoreConstants';
import {logger} from '@/helpers/logger';
import {isHistoryBack} from '@/helpers/navigationHistory';

const hasPerfectStoreSetupSnapshot = (data) =>
  Boolean(
    String(data?.name ?? '').trim() ||
    data?.targetType ||
    data?.activeStartDate ||
    (Array.isArray(data?.targetMediaFile) &&
      data.targetMediaFile.some((file) => file))
  );

const AddPerfectStore = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const isEdit = location?.state?.isEdit ?? false;
  const disableDraft = location?.state?.disableDraft ?? false;
  const fromWizard = location?.state?.fromWizard === true;
  const isDuplicate = location?.state?.isDuplicate === true;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const perfectStore = useAppSelector((state) => state?.perfectStore);
  const shouldStartFresh =
    !isEdit &&
    !fromWizard &&
    !isDuplicate &&
    !isHistoryBack(navigationType, location.key);
  const [formData, setFormData] = useState(() =>
    shouldStartFresh ? {...perfectStoreInitialFormData} : perfectStore?.formData
  );
  const [isCancel, setIsCancel] = useState(false);
  const originalNameRef = useRef(
    isEdit ? String(perfectStore?.formData?.name ?? '').trim() : ''
  );

  useEffect(() => {
    // Fresh create (Home / direct URL) must not keep persisted targetType.
    // Wizard step hops pass fromWizard so Setup can remount without wiping.
    if (!shouldStartFresh) return;
    dispatch(resetPerfectStore());
    setFormData({...perfectStoreInitialFormData});
  }, [dispatch, shouldStartFresh, isEdit, fromWizard, isDuplicate]);

  useEffect(() => {
    if (shouldStartFresh || hasPerfectStoreSetupSnapshot(formData)) return;
    if (!hasPerfectStoreSetupSnapshot(perfectStore?.formData)) return;
    setFormData(perfectStore.formData);
  }, [shouldStartFresh, formData, perfectStore?.formData]);

  const {
    handleDraft,
    handleNameValidation,
    isNameValid,
    setIsNameValid,
    handleValidationModalClose
  } = usePerfectStore();

  const getNavigationState = (returnTo) => ({
    isEdit,
    disableDraft,
    fromWizard: true,
    ...(location.pathname ? {from: location.pathname} : {}),
    ...(returnTo && {returnTo})
  });

  const getReviewRoute = () =>
    getPerfectStoreWizardReviewRoute({
      isEdit,
      id: perfectStore?.id
    });

  const validateName = async () => {
    const trimmedName = String(formData?.name ?? '').trim();

    if (!trimmedName) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Target Name is mandatory',
          type: 'error'
        })
      );
      return false;
    }

    if (isEdit && trimmedName === originalNameRef.current) {
      setIsNameValid(true);
      return true;
    }

    const params = {
      name: trimmedName,
      targetType: formData?.targetType ?? null,
      ...(perfectStore?.id != null && String(perfectStore.id) !== ''
        ? {id: perfectStore.id}
        : {}),
      ...(perfectStore?.uuid ? {uuid: perfectStore.uuid} : {})
    };
    const resp = await handleNameValidation(params);
    const isValid = resp?.success === true;
    setIsNameValid(isValid);
    return isValid;
  };

  const getDisplayRoute = () =>
    isStoreExecutionPepIrisTarget(formData?.targetType)
      ? ROUTES.PERFECT_STORE_PEPIRIS_DISPLAY
      : ROUTES.PERFECT_STORE_DISPLAY;

  const isPepIris = isStoreExecutionPepIrisTarget(formData?.targetType);

  const handleNextAction = async () => {
    if (!isPepIris) {
      const isValidName = await validateName();
      if (!isValidName) return;
    }

    dispatch(setPerfectStoreFormData(formData));
    navigate(getDisplayRoute(), {
      state: getNavigationState('setup')
    });
  };

  const handleDraftAction = async () => {
    try {
      const isValidName = await validateName();
      if (!isValidName) return;
      const response = await handleDraft(formData);
      if (response.success) {
        navigate(ROUTES.PERFECT_STORE);
      }
    } catch (error) {
      logger.error('Error saving Perfect Store draft:', error);
    }
  };

  const handleBeforeBack = () => {
    dispatch(setPerfectStoreFormData(formData));
  };

  const handleCancel = () => {
    setIsCancel(!isCancel);
  };

  const onClickPrimaryButton = () => {
    dispatch(resetPerfectStore());
    navigate(ROUTES.PERFECT_STORE);
  };

  const handleStepClick = (step) => {
    dispatch(setPerfectStoreFormData({...formData}));
    const stepRoutes = {
      2: getDisplayRoute(),
      3: getReviewRoute()
    };
    if (stepRoutes[step]) {
      navigate(stepRoutes[step], {
        state: getNavigationState(step === 2 ? 'setup' : undefined)
      });
    }
  };

  const getFormError = () =>
    isPepIris ? false : isPerfectStoreSetupInvalid(formData);

  const getCustomModal = (type) => {
    switch (type) {
      case 'CANCEL_MODAL':
        return getPerfectStoreCancelModal({
          show: isCancel,
          onConfirm: onClickPrimaryButton,
          onClose: handleCancel
        });
      case 'NAME_VALIDATION_MODAL':
        return getModal({
          title: 'Duplicate Name Detected',
          text: (
            <>
              It looks like there's already a Perfect Store with this name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          ),
          showPrimaryButton: true,
          primaryButtonText: 'Edit Name',
          onClickPrimaryButton: handleValidationModalClose,
          showSecondaryButton: false,
          showModal: !isNameValid,
          onCloseModal: handleValidationModalClose,
          hideCloseButton: true
        });
    }
  };

  return (
    <>
      <PerfectStoreWizardLayout
        currentStep={1}
        title={CREATE_PERFECT_STORE_TITLE}
        onStepClick={handleStepClick}
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
        disableDraft={disableDraft}
        beforeBack={handleBeforeBack}>
        <PerfectStoreSetUp formData={formData} setFormData={setFormData} />
      </PerfectStoreWizardLayout>
      {isCancel && getCustomModal('CANCEL_MODAL')}
      {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
    </>
  );
};

export default AddPerfectStore;
